import {
  forwardRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysLocaleMessages,
} from '../../provider/context';
import { resolveBaseAppearance } from './theme';
import type {
  BaseNumberInputChangeReason,
  BaseNumberInputProps,
} from './types';
import { cn } from './utils';

const decimalPattern = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;

export function parseBaseNumberInputValue(rawValue: string): number | null {
  if (!decimalPattern.test(rawValue)) return null;

  const parsedValue = Number(rawValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function resolveDecimalPlaces(value: number) {
  const match = /^(?:\d+)(?:\.(\d*))?(?:e([+-]?\d+))?$/i.exec(
    Math.abs(value).toString(),
  );
  if (!match) return 0;

  const fractionalPlaces = match[1]?.length ?? 0;
  const exponent = Number(match[2] ?? 0);
  return Math.min(12, Math.max(0, fractionalPlaces - exponent));
}

function normalizeSteppedValue(value: number, values: readonly number[]) {
  const precision = Math.max(...values.map(resolveDecimalPlaces));
  const scale = 10 ** precision;
  const normalized = Math.round((value + Number.EPSILON) * scale) / scale;
  return Object.is(normalized, -0) ? 0 : normalized;
}

export function resolveBaseNumberInputStep({
  rawValue,
  direction,
  step,
  min,
  max,
}: {
  rawValue: string;
  direction: -1 | 1;
  step: number;
  min?: number;
  max?: number;
}) {
  const parsedValue = parseBaseNumberInputValue(rawValue);
  const baseValue = parsedValue ?? 0;
  const precisionValues = [baseValue, step];
  if (min !== undefined) precisionValues.push(min);
  if (max !== undefined) precisionValues.push(max);

  let nextValue = normalizeSteppedValue(
    baseValue + direction * step,
    precisionValues,
  );
  if (min !== undefined) nextValue = Math.max(min, nextValue);
  if (max !== undefined) nextValue = Math.min(max, nextValue);
  return normalizeSteppedValue(nextValue, precisionValues);
}

function validateBoundary(name: 'min' | 'max', value: number | undefined) {
  if (value !== undefined && !Number.isFinite(value)) {
    throw new TypeError(`BaseNumberInput ${name} must be a finite number.`);
  }
}

function validateLabel(name: string, value: string) {
  if (!value.trim()) {
    throw new TypeError(`BaseNumberInput ${name} must be a non-empty string.`);
  }
}

function isInRange(
  value: number | null,
  min: number | undefined,
  max: number | undefined,
) {
  return (
    value !== null &&
    (min === undefined || value >= min) &&
    (max === undefined || value <= max)
  );
}

export const BaseNumberInput = forwardRef<
  HTMLInputElement,
  BaseNumberInputProps
>(function BaseNumberInput(
  {
    value,
    defaultValue = '',
    min,
    max,
    step = 1,
    showStepControls = false,
    incrementLabel,
    decrementLabel,
    invalid = false,
    disabled = false,
    readOnly = false,
    appearance,
    className,
    classNames,
    style,
    onChange,
    onKeyDown,
    onValueChange,
    'aria-invalid': nativeAriaInvalid,
    ...inputProps
  },
  ref,
) {
  if (value !== undefined && typeof value !== 'string') {
    throw new TypeError('BaseNumberInput value must be a string.');
  }
  if (typeof defaultValue !== 'string') {
    throw new TypeError('BaseNumberInput defaultValue must be a string.');
  }
  validateBoundary('min', min);
  validateBoundary('max', max);
  if (min !== undefined && max !== undefined && min > max) {
    throw new RangeError(
      'BaseNumberInput min must be less than or equal to max.',
    );
  }
  if (!Number.isFinite(step) || step <= 0) {
    throw new TypeError('BaseNumberInput step must be a positive finite number.');
  }
  for (const [name, candidate] of [
    ['showStepControls', showStepControls],
    ['invalid', invalid],
    ['disabled', disabled],
    ['readOnly', readOnly],
  ] as const) {
    if (typeof candidate !== 'boolean') {
      throw new TypeError(`BaseNumberInput ${name} must be a boolean.`);
    }
  }

  const messages = useMonkeysLocaleMessages();
  const resolvedIncrementLabel =
    incrementLabel ?? messages.numberInput.increment;
  const resolvedDecrementLabel =
    decrementLabel ?? messages.numberInput.decrement;
  validateLabel('incrementLabel', resolvedIncrementLabel);
  validateLabel('decrementLabel', resolvedDecrementLabel);

  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const isControlled = value !== undefined;
  const rawValue = isControlled ? value : uncontrolledValue;
  const parsedValue = parseBaseNumberInputValue(rawValue);
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);

  const emitValueChange = (
    nextRawValue: string,
    reason: BaseNumberInputChangeReason,
  ) => {
    if (!isControlled) setUncontrolledValue(nextRawValue);
    const nextValue = parseBaseNumberInputValue(nextRawValue);
    onValueChange?.(nextValue, {
      rawValue: nextRawValue,
      reason,
      inRange: isInRange(nextValue, min, max),
    });
  };

  const stepValue = (direction: -1 | 1) => {
    if (disabled || readOnly) return;
    if (
      parsedValue !== null &&
      ((direction === 1 && max !== undefined && parsedValue >= max) ||
        (direction === -1 && min !== undefined && parsedValue <= min))
    ) {
      return;
    }

    const nextValue = resolveBaseNumberInputStep({
      rawValue,
      direction,
      step,
      min,
      max,
    });
    emitValueChange(
      String(nextValue),
      direction === 1 ? 'step-up' : 'step-down',
    );
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);
    emitValueChange(event.currentTarget.value, 'input');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || disabled || readOnly) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

    event.preventDefault();
    stepValue(event.key === 'ArrowUp' ? 1 : -1);
  };

  const decrementDisabled =
    disabled ||
    readOnly ||
    (parsedValue !== null && min !== undefined && parsedValue <= min);
  const incrementDisabled =
    disabled ||
    readOnly ||
    (parsedValue !== null && max !== undefined && parsedValue >= max);

  return (
    <div
      {...componentAttributes}
      data-monkeys-component="number-input"
      data-slot="root"
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
      data-readonly={readOnly || undefined}
      className={cn(
        theme.slots.controlWrapper,
        invalid && theme.slots.controlInvalid,
        disabled && theme.slots.controlDisabled,
        readOnly && theme.slots.controlReadOnly,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.control,
        ...(invalid ? theme.styles.controlInvalid : undefined),
        ...(disabled ? theme.styles.controlDisabled : undefined),
        ...style,
      }}
    >
      <input
        {...inputProps}
        ref={ref}
        type="text"
        inputMode="decimal"
        role="spinbutton"
        value={rawValue}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={nativeAriaInvalid ?? (invalid || undefined)}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={parsedValue ?? undefined}
        aria-valuetext={rawValue || undefined}
        data-slot="control"
        className={cn(
          theme.slots.controlBase,
          theme.slots.controlInput,
          theme.slots.numberInputControl,
          classNames?.control,
        )}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {showStepControls ? (
        <span
          data-slot="step-controls"
          className={cn(
            theme.slots.numberInputStepControls,
            classNames?.stepControls,
          )}
          style={theme.styles.numberInputStepControls}
        >
          <button
            type="button"
            aria-label={resolvedIncrementLabel}
            disabled={incrementDisabled}
            data-slot="increment"
            className={cn(
              theme.slots.numberInputStepButton,
              classNames?.increment,
            )}
            style={theme.styles.numberInputStepButton}
            onClick={() => stepValue(1)}
          >
            +
          </button>
          <button
            type="button"
            aria-label={resolvedDecrementLabel}
            disabled={decrementDisabled}
            data-slot="decrement"
            className={cn(
              theme.slots.numberInputStepButton,
              classNames?.decrement,
            )}
            style={theme.styles.numberInputStepButton}
            onClick={() => stepValue(-1)}
          >
            −
          </button>
        </span>
      ) : null}
    </div>
  );
});
